/**
 * Public CV Detail Page
 * Uses shared components with CVDetailPage to avoid code duplication.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, Card, CardContent, Tabs, TabsList, TabsTrigger, TabsContent } from '@aaas/shared';
import { AlertCircle, ArrowLeft, Loader2, Mail, Phone, User, FileText } from 'lucide-react';
import { PDFViewer } from '../../components/PDFViewer';
import { NotesSection } from '../../components/NotesSection';
import { CVInfoDisplay } from '../../components/cv-detail';
import { ReviewStatusButtons, ReviewStatusBadge } from '../../components/ReviewStatusButtons';
import shareService from '../../services/share.service';
import type { CVNote, CVNoteListResponse, PublicCVDetail, ReviewStatus } from '../../types';
import type { JsonResume } from '../../types';

export function PublicCVDetailPage() {
  const { token, cvId } = useParams<{ token: string; cvId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('ats');

  // State
  const [cvDetail, setCVDetail] = useState<PublicCVDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state - default to "parsed" like CVDetailPage
  const [activeTab, setActiveTab] = useState<string>('parsed');

  // Notes state
  const [notes, setNotes] = useState<CVNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  // Note form state
  const [noteContent, setNoteContent] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  // Selected attachment for PDF viewer
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);

  // Fetch CV detail
  useEffect(() => {
    if (!token || !cvId) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await shareService.getSharedCVDetail(token, cvId);
        setCVDetail(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('public.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, cvId]);

  // Fetch notes
  useEffect(() => {
    if (!token || !cvId) return;

    const fetchNotes = async () => {
      setLoadingNotes(true);

      try {
        const notesData: CVNoteListResponse = await shareService.listPublicNotes(token, cvId);
        setNotes(notesData.items);
      } catch (err) {
        console.error('Failed to fetch notes:', err);
      } finally {
        setLoadingNotes(false);
      }
    };

    fetchNotes();
  }, [token, cvId]);

  // Handle submit note
  const handleSubmitNote = useCallback(async () => {
    if (!token || !cvId || !noteContent.trim()) return;

    setIsSubmittingNote(true);

    try {
      // Get token if user is logged in
      const idToken = user ? await user.getIdToken() : undefined;

      const newNote = await shareService.createPublicNote(
        token,
        cvId,
        {
          content: noteContent.trim(),
          creator_name: idToken ? undefined : creatorName.trim() || undefined,
        },
        idToken
      );

      // Add note to list
      setNotes((prev) => [newNote, ...prev]);

      // Update note count in CV detail
      setCVDetail((prev) => {
        if (!prev) return prev;
        return { ...prev, note_count: prev.note_count + 1 };
      });

      // Reset form (keep creatorName for convenience)
      setNoteContent('');
      if (cvId) {
        localStorage.removeItem(`cv-note-draft-share-${cvId}`);
      }
    } catch (err) {
      console.error('Failed to submit note:', err);
      alert(err instanceof Error ? err.message : t('public.loadFailed'));
    } finally {
      setIsSubmittingNote(false);
    }
  }, [token, cvId, noteContent, creatorName, user, t]);

  // Handle review status change
  const handleReviewStatusChange = useCallback(
    async (newStatus: ReviewStatus) => {
      if (!token || !cvId || !cvDetail) return;

      const oldStatus = cvDetail.review_status;

      // Optimistic update
      setCVDetail((prev) => (prev ? { ...prev, review_status: newStatus } : prev));

      try {
        await shareService.updateReviewStatus(token, cvId, newStatus);
      } catch (err) {
        console.error('Failed to update review status:', err);
        // Revert on error
        setCVDetail((prev) => (prev ? { ...prev, review_status: oldStatus } : prev));
      }
    },
    [token, cvId, cvDetail]
  );

  // Handle back navigation
  const handleBack = () => {
    navigate(`/ats/public/share/${token}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-2 text-muted-foreground">{t('public.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !cvDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="mt-4 text-lg font-semibold">
                {error || t('public.cvDetail.cvNotFound')}
              </h2>
              <button
                onClick={handleBack}
                className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('public.cvDetail.goBack')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get viewable attachments (PDF and DOCX)
  const viewableAttachments = cvDetail.attachments.filter((att) => {
    const type = att.file_type.toLowerCase();
    return type === 'pdf' || type === 'docx';
  });
  const currentAttachment = viewableAttachments[selectedAttachmentIndex];
  const hasViewableAttachments = viewableAttachments.length > 0;

  // Parse parsed_data as JsonResume
  const parsedData = cvDetail.parsed_data as JsonResume | null;
  const basics = parsedData?.basics;
  const displayLabel = basics?.label;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {basics?.image ? (
                <img
                  src={basics.image}
                  alt={cvDetail.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-primary" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold truncate">{cvDetail.name}</h1>
                  <ReviewStatusBadge status={cvDetail.review_status} />
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {displayLabel && <span className="truncate">{displayLabel}</span>}
                  {cvDetail.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {cvDetail.email}
                    </span>
                  )}
                  {cvDetail.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {cvDetail.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <ReviewStatusButtons
                status={cvDetail.review_status}
                onStatusChange={handleReviewStatusChange}
                size="default"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main content - 2 column layout like CVDetailPage */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - CV Info with Tabs (2/3) */}
          <div className="lg:col-span-2">
            {hasViewableAttachments ? (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <TabsList>
                    <TabsTrigger value="parsed">{t('public.cvDetail.tabGeneral')}</TabsTrigger>
                    <TabsTrigger value="original">{t('public.cvDetail.tabResume')}</TabsTrigger>
                  </TabsList>

                  {/* Attachment Selector - only show if multiple viewable files and on original tab */}
                  {viewableAttachments.length > 1 && activeTab === 'original' && (
                    <select
                      value={selectedAttachmentIndex}
                      onChange={(e) => setSelectedAttachmentIndex(Number(e.target.value))}
                      className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
                    >
                      {viewableAttachments.map((att, idx) => (
                        <option key={att.id} value={idx}>
                          {att.file_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <TabsContent value="parsed" className="mt-0">
                  {parsedData ? (
                    <CVInfoDisplay data={parsedData} />
                  ) : (
                    <Card>
                      <CardContent className="py-12">
                        <div className="text-center text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{t('public.cvDetail.noCvInfo')}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="original" className="mt-0">
                  <Card className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="h-[calc(100vh-200px)]">
                        {currentAttachment ? (
                          <PDFViewer
                            url={currentAttachment.signed_url}
                            fileName={currentAttachment.file_name}
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                              <p>{t('public.cvDetail.noPdfFile')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            ) : parsedData ? (
              // No viewable attachments - just show parsed data without tabs
              <CVInfoDisplay data={parsedData} />
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{t('public.cvDetail.noCvInfo')}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column - Notes (1/3) */}
          <div className="lg:col-span-1">
            <NotesSection
              notes={notes}
              isLoading={loadingNotes}
              isSubmitting={isSubmittingNote}
              noteContent={noteContent}
              onNoteContentChange={setNoteContent}
              onSubmitNote={handleSubmitNote}
              creatorNameInput={creatorName}
              onCreatorNameChange={setCreatorName}
              showCreatorNameField={!user}
              currentUserName={user?.displayName || undefined}
              title={t('public.cvDetail.notes')}
              noteCount={cvDetail.note_count}
              draftKey={cvId ? `cv-note-draft-share-${cvId}` : undefined}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
